import { useCallback, useRef, useState, useEffect } from "react";
import useDebouncedCallback from "theme/components/helpers/useDebouncedCallback";
import useAutocomplete from "theme/components/organisms/Autocomplete/useAutocomplete";
import useSearchBarNavigation from "theme/modules/Search/SearchBar/useSearchBarNavigation";

const useSearchBar = ({ search, setSearch, data, onFocusRequest }) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef(null);
  const searchBarRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (
        isOpen &&
        searchBarRef.current instanceof HTMLElement &&
        !searchBarRef.current.contains(target) &&
        contentRef.current instanceof HTMLElement &&
        !contentRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const [selected, onSelect] = useAutocomplete(
    contentRef,
    isOpen,
    data?.searchSuggestions?.query,
    "all",
    useCallback(() => {
      setSearch({ search: "" });
      setIsOpen(false);
    }, [setSearch])
  );

  const { searchUrl, searchTarget, onSubmit } = useSearchBarNavigation(search);

  const setSearchDebounced = useDebouncedCallback(setSearch, 300);
  const onChange = (data) => {
    setSearchDebounced(data);
    setIsOpen(data?.search?.length > 0);
  };
  const onFocus = () => {
    if (search?.search?.length > 0) {
      setIsOpen(true);
    }
  };

  const showAutocompleteResults = data && !data.loading;

  const focusInput = useCallback(() => {
    if (inputRef.current instanceof HTMLElement) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (onFocusRequest) {
      focusInput();
    }
  }, [onFocusRequest, focusInput]);

  return {
    contentRef,
    searchBarRef,
    inputRef,
    focusInput,
    searchUrl,
    searchTarget,
    onSubmit,
    onChange,
    onFocus,
    showAutocompleteResults,
    isOpen,
    setIsOpen,
    selected,
    onSelect,
  };
};

export default useSearchBar;
